"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { Icon, Pill } from "@/components/Icons";
import { useStock } from "@/context/StockContext";
import { euro } from "@/lib/utils";

type FournisseurRow = {
  nom: string;
  factures: number;
  montantTtc: number;
  lignes: number;
  entreEnStock: boolean;
  ignorerFrais: boolean;
  defaut: boolean;
  enregistre: boolean;
};

export function FournisseursConfigView() {
  const { showToast } = useStock();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<FournisseurRow[]>([]);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [filtre, setFiltre] = useState<"tous" | "stock" | "exclus">("tous");
  const [saving, setSaving] = useState<string | null>(null);

  const sync = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/pennylane/fournisseurs");
      const data = await res.json();
      if (!res.ok) {
        showToast(data.error || "Impossible de charger les fournisseurs");
        return;
      }
      setRows(data.fournisseurs ?? []);
      setUpdatedAt(data.updatedAt ?? null);
      showToast(
        `${data.stats?.total ?? 0} fournisseurs · ${data.stats?.enStock ?? 0} en stock · ${data.stats?.exclus ?? 0} exclus`,
      );
    } catch {
      showToast("Erreur réseau");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    void sync();
  }, [sync]);

  const patch = async (
    nom: string,
    patch: { entreEnStock?: boolean; ignorerFrais?: boolean },
  ) => {
    setSaving(nom);
    setRows((prev) =>
      prev.map((r) => (r.nom === nom ? { ...r, ...patch, enregistre: true } : r)),
    );
    try {
      const res = await fetch("/api/pennylane/fournisseurs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nom, ...patch }),
      });
      const data = await res.json();
      if (!res.ok) {
        showToast(data.error || "Enregistrement impossible");
        void sync();
        return;
      }
      setUpdatedAt(data.updatedAt ?? null);
    } catch {
      showToast("Erreur réseau");
      void sync();
    } finally {
      setSaving(null);
    }
  };

  const qn = q.trim().toLowerCase();
  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (filtre === "stock" && !r.entreEnStock) return false;
      if (filtre === "exclus" && r.entreEnStock) return false;
      if (!qn) return true;
      return r.nom.toLowerCase().includes(qn);
    });
  }, [rows, filtre, qn]);

  const stats = useMemo(
    () => ({
      total: rows.length,
      enStock: rows.filter((r) => r.entreEnStock).length,
      exclus: rows.filter((r) => !r.entreEnStock).length,
    }),
    [rows],
  );

  return (
    <>
      <p className="muted" style={{ maxWidth: "70ch", marginBottom: 14 }}>
        Indique pour chaque fournisseur si ses produits <b>entrent en stock</b>. Les transporteurs
        (Geodis, Chronopost…) sont exclus par défaut. Tu peux aussi ignorer les lignes de frais
        (port, emballage) même chez un fournisseur « stock ».
      </p>

      <div className="assoc-meta">
        <span>
          Stockage : <b className="mono">data/fournisseurs.json</b>
        </span>
        <span>
          Total : <b>{stats.total}</b>
        </span>
        <span>
          En stock : <b>{stats.enStock}</b>
        </span>
        <span>
          Exclus : <b>{stats.exclus}</b>
        </span>
        {updatedAt && (
          <span>
            Dernier enreg. :{" "}
            <b>
              {new Date(updatedAt).toLocaleString("fr-FR", {
                day: "2-digit",
                month: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </b>
          </span>
        )}
      </div>

      <div className="toolbar" style={{ marginBottom: 10 }}>
        <div className="search">
          <Icon name="search" />
          <input
            type="search"
            placeholder="Nom du fournisseur…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="chips">
          {(
            [
              ["tous", `Tous (${stats.total})`],
              ["stock", `En stock (${stats.enStock})`],
              ["exclus", `Exclus (${stats.exclus})`],
            ] as const
          ).map(([id, lib]) => (
            <button
              key={id}
              className="chip"
              type="button"
              aria-pressed={filtre === id}
              onClick={() => setFiltre(id)}
            >
              {lib}
            </button>
          ))}
        </div>
        <button className="btn small" type="button" disabled={loading} onClick={() => void sync()}>
          <Icon name="sync" /> {loading ? "…" : "Actualiser"}
        </button>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Fournisseur</th>
              <th>Factures</th>
              <th>TTC</th>
              <th>Entre en stock</th>
              <th>Ignorer frais</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((f) => (
              <tr key={f.nom} className={!f.entreEnStock ? "row-dim" : undefined}>
                <td>
                  <div style={{ fontWeight: 600 }}>{f.nom}</div>
                  <div className="muted mono" style={{ fontSize: ".75rem" }}>
                    {f.lignes} lignes lues
                    {!f.defaut && f.entreEnStock === false ? " · transport détecté" : ""}
                    {f.enregistre ? "" : " · défaut"}
                  </div>
                </td>
                <td className="mono">{f.factures}</td>
                <td className="mono">{euro(f.montantTtc)}</td>
                <td>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={f.entreEnStock}
                      disabled={saving === f.nom}
                      onChange={(e) => void patch(f.nom, { entreEnStock: e.target.checked })}
                    />
                    <span>
                      {f.entreEnStock ? (
                        <Pill kind="ok">Oui</Pill>
                      ) : (
                        <Pill kind="warn">Non</Pill>
                      )}
                    </span>
                  </label>
                </td>
                <td>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={f.ignorerFrais}
                      disabled={saving === f.nom || !f.entreEnStock}
                      onChange={(e) => void patch(f.nom, { ignorerFrais: e.target.checked })}
                    />
                    <span className="muted" style={{ fontSize: ".82rem" }}>
                      {f.ignorerFrais ? "Port / emballage exclus" : "Tout garder"}
                    </span>
                  </label>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!filtered.length && !loading && (
          <p className="muted" style={{ padding: 14 }}>
            {rows.length
              ? "Aucun fournisseur pour ce filtre."
              : "Aucun achat importé — lance Synchroniser d’abord."}
          </p>
        )}
      </div>
    </>
  );
}
